const API_BASE = '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'same-origin',
  });

  if (res.status === 401) {
    // Redirect to login if unauthorized
    if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/portal')) {
      window.location.href = '/login';
    }
    throw new ApiError('No autorizado', 401);
  }

  const data: Record<string, unknown> = await res.json();

  if (!res.ok) {
    throw new ApiError((data.error as string) ?? 'Error desconocido', res.status);
  }

  return data as T;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
}

export const auth = {
  login: (email: string, password: string, turnstileToken?: string) =>
    request<{ user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    }),

  logout: () =>
    request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  me: () =>
    request<{ user: User }>('/auth/me'),
};

// ─── Clients ─────────────────────────────────────────────────────────────────

export interface AssignedUser {
  id: number;
  name: string;
  photo_key: string | null;
}

export interface Client {
  id: number;
  business_name: string;
  domain: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_phones: string | null;
  contact_emails: string | null;
  social_links: string | null;
  business_intel: string | null;
  notes: string | null;
  status: 'cold_lead' | 'warm_lead' | 'active' | 'churned';
  latest_audit_score: number | null;
  latest_audit_id: number | null;
  logo_key: string | null;
  assigned_user_id: number | null;
  count_in_stats: number;
  created_at: string;
  updated_at: string;
  // Multi-assignment from client_assignments table
  assigned_users?: AssignedUser[];
}

export interface ClientsResponse {
  clients: Client[];
  total: number;
  page: number;
  limit: number;
}

export const clients = {
  list: (params?: { status?: string; search?: string; page?: number }) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.search) sp.set('search', params.search);
    if (params?.page) sp.set('page', String(params.page));
    return request<ClientsResponse>(`/clients?${sp.toString()}`);
  },

  get: (id: number) =>
    request<{ client: Client; audits: Audit[]; tokens: ClientToken[]; proposals: Proposal[]; assignments: AssignedUser[] }>(`/clients/${id}`),

  create: (data: Partial<Client>) =>
    request<{ client: Client }>('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<Client>) =>
    request<{ client: Client }>(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request<{ ok: boolean }>(`/clients/${id}`, { method: 'DELETE' }),

  generateToken: (id: number, label?: string, expiresDays?: number) =>
    request<{ token: string; url: string; expires_at: string }>(`/clients/${id}/token`, {
      method: 'POST',
      body: JSON.stringify({ label, expires_days: expiresDays }),
    }),

  getAssignments: (id: number) =>
    request<{ assignments: AssignedUser[] }>(`/clients/${id}/assignments`),

  setAssignments: (id: number, userIds: number[]) =>
    request<{ assignments: AssignedUser[] }>(`/clients/${id}/assignments`, {
      method: 'PUT',
      body: JSON.stringify({ user_ids: userIds }),
    }),

  overview: (id: number) =>
    request<TenantOverview>(`/clients/${id}/overview`),
};

// ─── Tenant Overview (super-admin) ─────────────────────────────────────────────

export interface TenantOwner {
  id: number;
  name: string;
  email: string;
  last_login_at: string | null;
  created_at: string;
}

export interface TenantOnboarding {
  status: string | null;
  legal_name: string | null;
  tax_id: string | null;
  contact_phone: string | null;
  contact_whatsapp: string | null;
  property_count: number | null;
  property_type: string | null;
  setup_mode: string | null;
  current_website: string | null;
  listing_booking: string | null;
  listing_airbnb: string | null;
  listing_vrbo: string | null;
  listing_other: string | null;
  listing_urls?: string[];
  plan_units: number | null;
  plan_billing: string | null;
  site_model: string | null;
  payment_status: string | null;
  paid_at: string | null;
  submitted_at: string | null;
  updated_at: string | null;
}

export interface TenantAgreement {
  doc_title: string;
  doc_version: string;
  accepted_name: string;
  accepted_at: string;
}

export interface TenantProperty {
  id: number;
  name: string;
  property_type: string | null;
  city: string | null;
  max_guests: number | null;
  bedrooms: number | null;
  base_price: number | null;
  status: string;
  amenities_json: string | null;
  photo_count: number;
}

export interface TenantBooking {
  id: number;
  code: string | null;
  guest_name: string;
  property_name: string | null;
  check_in: string;
  check_out: string;
  nights: number;
  guests: number;
  total: number;
  currency: string | null;
  status: string;
  channel: string;
  created_at: string;
}

export interface TenantSiteStatus {
  site_slug: string | null;
  published_at: string | null;
  updated_at: string | null;
  has_draft: number;
  has_published: number;
}

export interface TenantFeedback {
  id: number;
  message: string;
  page: string | null;
  status: string | null;
  created_at: string;
}

export interface TenantOverview {
  client: {
    id: number;
    business_name: string;
    domain: string | null;
    status: string;
    onboarding_status: string | null;
    stripe_account_id: string | null;
    stripe_account_status: string | null;
    notes: string | null;
    created_at: string;
  };
  owners: TenantOwner[];
  onboarding: TenantOnboarding | null;
  agreement: TenantAgreement | null;
  properties: TenantProperty[];
  bookings: TenantBooking[];
  booking_stats: { total: number; revenue: number; confirmed: number };
  site: TenantSiteStatus | null;
  feedback: TenantFeedback[];
}

// ─── Audits ──────────────────────────────────────────────────────────────────

export interface Audit {
  id: number;
  client_id: number | null;
  url: string;
  domain: string;
  business_name: string;
  overall_score: number | null;
  perf_score_mobile: number | null;
  perf_score_desktop: number | null;
  seo_score_mobile: number | null;
  accessibility_score_mobile: number | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  report_html_key: string | null;
  report_pdf_key: string | null;
  screenshots_key: string | null;
  raw_data: string | null;
  error_message: string | null;
  assigned_user_id: number | null;
  created_at: string;
  updated_at: string;
  client_business_name?: string;
  client_logo_key?: string | null;
  assigned_user_name?: string;
  assigned_user_photo?: string | null;
}

export interface AuditsResponse {
  audits: Audit[];
  total: number;
  page: number;
  limit: number;
}

export const audits = {
  list: (params?: { client_id?: number; status?: string; search?: string; page?: number }) => {
    const sp = new URLSearchParams();
    if (params?.client_id) sp.set('client_id', String(params.client_id));
    if (params?.status) sp.set('status', params.status);
    if (params?.search) sp.set('search', params.search);
    if (params?.page) sp.set('page', String(params.page));
    return request<AuditsResponse>(`/audits?${sp.toString()}`);
  },

  get: (id: number) =>
    request<{ audit: Audit }>(`/audits/${id}`),

  create: (data: { url: string; business_name?: string; client_id?: number }) =>
    request<{ audit: Audit }>('/audits', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<Audit>) =>
    request<{ audit: Audit }>(`/audits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request<{ ok: boolean }>(`/audits/${id}`, { method: 'DELETE' }),

  bulkDelete: (ids: number[]) =>
    request<{ ok: boolean; deleted: number }>('/audits', {
      method: 'DELETE',
      body: JSON.stringify({ ids }),
    }),

  upload: async (id: number, formData: FormData) => {
    const res = await fetch(`${API_BASE}/audits/${id}/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    });
    if (!res.ok) throw new ApiError('Upload failed', res.status);
    return res.json();
  },

  downloadUrl: (id: number, type: 'pdf' | 'html' | 'screenshots' = 'pdf') =>
    `${API_BASE}/audits/${id}/download?type=${type}`,

  run: (id: number) =>
    request<{ ok: boolean; message: string }>(`/audits/${id}/run`, {
      method: 'POST',
    }),
};

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface DashboardStats {
  stats: {
    total_clients: number;
    clients_by_status: { status: string; count: number }[];
    total_audits: number;
    audits_by_status: { status: string; count: number }[];
    avg_score: number | null;
  };
  recent_audits: Audit[];
  recent_activity: {
    id: number;
    user_name: string;
    action: string;
    entity_type: string;
    entity_id: number;
    details: string;
    created_at: string;
  }[];
}

export const dashboard = {
  get: () => request<DashboardStats>('/dashboard'),
};

// ─── Portal (public) ─────────────────────────────────────────────────────────

export interface PortalData {
  client: {
    business_name: string;
    domain: string;
    status: string;
  };
  audits: Audit[];
  proposals: PortalProposal[];
}

export interface PortalProposal {
  id: number;
  title: string;
  status: string;
  discount_percent: number;
  notes: string | null;
  valid_until: string | null;
  created_at: string;
  items: ProposalItem[];
}

export const portal = {
  get: (token: string) => request<PortalData>(`/portal/${token}`),
  downloadUrl: (token: string, auditId: number) =>
    `${API_BASE}/portal/${token}/download/${auditId}`,
  trackEvent: (token: string, eventType: string, eventData?: Record<string, unknown>) =>
    fetch(`${API_BASE}/portal/${token}/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: eventType, event_data: eventData }),
    }),
};

export interface ClientToken {
  id: number;
  token: string;
  label: string | null;
  expires_at: string | null;
  last_accessed_at: string | null;
  created_at: string;
}

// ─── Proposals ───────────────────────────────────────────────────────────────

export interface Proposal {
  id: number;
  client_id: number;
  audit_id: number | null;
  title: string;
  status: 'draft' | 'sent' | 'viewed' | 'accepted' | 'rejected';
  discount_percent: number;
  discount_code: string | null;
  discount_type: 'percent' | 'fixed';
  discount_deadline: string | null;
  notes: string | null;
  valid_until: string | null;
  payment_mode: 'standard' | 'subscription';
  interest_rate: number;
  subscription_interval: 'weekly' | 'monthly' | 'yearly' | null;
  subscription_amount: number | null;
  created_at: string;
  updated_at: string;
  items?: ProposalItem[];
}

export interface ProposalItem {
  id: number;
  proposal_id: number;
  service_id: string;
  service_name: string;
  tier: string | null;
  description: string | null;
  price: number;
  is_recurring: number;
  sort_order: number;
  quantity?: number;
}

export interface PortalEvent {
  id: number;
  client_id: number;
  event_type: string;
  event_data: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export const proposals = {
  get: (id: number) =>
    request<{ proposal: Proposal & { items: ProposalItem[] } }>(`/proposals/${id}`),

  create: (data: { client_id: number; audit_id?: number; title?: string }) =>
    request<{ proposal: Proposal & { items: ProposalItem[] } }>('/proposals', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<Pick<Proposal, 'title' | 'discount_percent' | 'discount_code' | 'discount_type' | 'discount_deadline' | 'notes' | 'valid_until' | 'status' | 'payment_mode' | 'subscription_interval' | 'subscription_amount'>>) =>
    request<{ proposal: Proposal }>(`/proposals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    request<{ ok: boolean }>(`/proposals/${id}`, { method: 'DELETE' }),

  addItem: (proposalId: number, item: Omit<ProposalItem, 'id' | 'proposal_id'>) =>
    request<{ item: ProposalItem }>(`/proposals/${proposalId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    }),

  updateItem: (proposalId: number, itemId: number, data: Partial<ProposalItem>) =>
    request<{ item: ProposalItem }>(`/proposals/${proposalId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteItem: (proposalId: number, itemId: number) =>
    request<{ ok: boolean }>(`/proposals/${proposalId}/items/${itemId}`, { method: 'DELETE' }),

  generateFromAudit: (auditId: number) =>
    request<{ proposal: Proposal & { items: ProposalItem[] } }>(`/proposals/generate/${auditId}`, {
      method: 'POST',
    }),
};

// ─── Storage (R2) ────────────────────────────────────────────────────────────

export interface StorageFile {
  key: string;
  size: number;
  uploaded: string;
  httpMetadata?: { contentType?: string };
}

export interface R2Usage {
  month: string;
  class_a_ops: number;
  class_b_ops: number;
  storage_bytes: number;
  limits: {
    storage_bytes: number;
    class_a_ops: number;
    class_b_ops: number;
  };
  percentages: {
    storage: number;
    class_a: number;
    class_b: number;
  };
  alerts: ('storage' | 'class_a' | 'class_b')[];
}

export interface StorageListResponse {
  files: StorageFile[];
  total: number;
  usage: R2Usage;
}

export const storage = {
  list: (prefix?: string) => {
    const sp = new URLSearchParams();
    if (prefix) sp.set('prefix', prefix);
    return request<StorageListResponse>(`/storage?${sp.toString()}`);
  },

  usage: () =>
    request<{ usage: R2Usage }>('/storage?usage_only=true'),

  download: (key: string) =>
    `${API_BASE}/storage/file?key=${encodeURIComponent(key)}`,

  inlineUrl: (key: string) =>
    `${API_BASE}/storage/file?key=${encodeURIComponent(key)}&inline`,

  deleteFile: (key: string) =>
    request<{ ok: boolean }>(`/storage/file?key=${encodeURIComponent(key)}`, { method: 'DELETE' }),

  bulkDelete: (keys: string[]) =>
    request<{ ok: boolean; deleted: number }>('/storage', {
      method: 'DELETE',
      body: JSON.stringify({ keys }),
    }),
};

// ─── Projects (Admin) ────────────────────────────────────────────────────────

export interface Project {
  id: number;
  client_id: number;
  proposal_id: number | null;
  title: string;
  description: string | null;
  status: 'pending_payment' | 'active' | 'paused' | 'completed' | 'cancelled';
  total_amount: number;
  upfront_percent: number;
  currency: string;
  assigned_user_id: number | null;
  count_in_stats: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // joined fields
  client_business_name?: string;
  total_milestones?: number;
  paid_milestones?: number;
  assigned_user_name?: string;
  assigned_user_photo?: string | null;
}

export interface Milestone {
  id: number;
  project_id: number;
  title: string;
  description: string | null;
  sort_order: number;
  amount: number;
  percentage: number;
  status: 'pending' | 'in_progress' | 'completed' | 'payment_requested' | 'paid' | 'skipped';
  is_upfront: number;
  is_final: number;
  due_date: string | null;
  completed_at: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface TimelineEvent {
  id: number;
  project_id: number;
  milestone_id: number | null;
  event_type: string;
  title: string;
  description: string | null;
  metadata: string | null;
  is_client_visible: number;
  created_by_type: string;
  created_by_id: number | null;
  created_at: string;
}

export const projects = {
  list: (params?: { client_id?: number; status?: string }) => {
    const sp = new URLSearchParams();
    if (params?.client_id) sp.set('client_id', String(params.client_id));
    if (params?.status) sp.set('status', params.status);
    return request<{ projects: Project[] }>(`/projects?${sp.toString()}`);
  },

  get: (id: number) =>
    request<{
      project: Project;
      milestones: Milestone[];
      timeline: TimelineEvent[];
      payments: Payment[];
    }>(`/projects/${id}`),

  create: (data: { client_id: number; title: string; description?: string; total_amount: number; upfront_percent?: number; proposal_id?: number }) =>
    request<{ project: Project }>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<Pick<Project, 'title' | 'description' | 'status' | 'total_amount' | 'upfront_percent' | 'count_in_stats'>>) =>
    request<{ project: Project }>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  addMilestone: (projectId: number, data: { title: string; description?: string; amount: number; percentage?: number; due_date?: string }) =>
    request<{ milestone: Milestone }>(`/projects/${projectId}/milestones`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMilestone: (projectId: number, milestoneId: number, data: Partial<Pick<Milestone, 'title' | 'description' | 'amount' | 'percentage' | 'status' | 'due_date'>>) =>
    request<{ milestone: Milestone }>(`/projects/${projectId}/milestones/${milestoneId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteMilestone: (projectId: number, milestoneId: number) =>
    request<{ ok: boolean }>(`/projects/${projectId}/milestones/${milestoneId}`, { method: 'DELETE' }),

  addTimelineEvent: (projectId: number, data: { event_type: string; title: string; description?: string; is_client_visible?: boolean; milestone_id?: number }) =>
    request<{ event: TimelineEvent }>(`/projects/${projectId}/timeline`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  inviteClient: (clientId: number) =>
    request<{ ok: boolean }>(`/clients/${clientId}/invite`, { method: 'POST' }),
};

// ─── Payments (Admin) ────────────────────────────────────────────────────────

export interface Payment {
  id: number;
  client_id: number;
  project_id: number | null;
  milestone_id: number | null;
  subscription_id: number | null;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'refunded' | 'cancelled';
  payment_method: string | null;
  description: string | null;
  invoice_number: string | null;
  paid_at: string | null;
  created_at: string;
  // joined fields
  project_title?: string;
  milestone_title?: string;
}

// ─── Team ────────────────────────────────────────────────────────────────────

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  photo_key: string | null;
}

export const team = {
  list: () => request<{ team: TeamMember[] }>('/team'),
};

// ─── Settings ────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  photo_key: string | null;
  created_at: string;
}

export const settings = {
  get: () => request<{ user: UserProfile }>('/settings'),

  update: (data: { name?: string; photo_key?: string }) =>
    request<{ user: UserProfile }>('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: boolean }>('/settings', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),
};

// ─── Invoices (Admin) ────────────────────────────────────────────────────────

export interface InvoiceSettings {
  company_name: string;
  company_nif: string;
  company_address: string;
  company_city: string;
  company_postal_code: string;
  company_country: string;
  company_email: string;
  company_phone: string;
  company_website: string;
  invoice_prefix: string;
  tax_rate: number;
  tax_name: string;
  notes_template: string;
}

export const invoices = {
  generate: (paymentId: number) =>
    request<{ invoice_number: string; invoice_pdf_key: string }>('/invoices', {
      method: 'POST',
      body: JSON.stringify({ payment_id: paymentId }),
    }),

  list: () =>
    request<{ invoices: Array<Payment & { business_name: string }> }>('/invoices'),

  downloadUrl: (paymentId: number) => `/api/invoices/${paymentId}/download`,

  settings: {
    get: () => request<{ settings: InvoiceSettings }>('/invoice-settings'),
    update: (data: Partial<InvoiceSettings>) =>
      request<{ ok: boolean }>('/invoice-settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },
};

// ─── Client Portal API ───────────────────────────────────────────────────────

export interface PortalDashboardData {
  audits: Array<{ id: number; domain: string; overall_score: number | null; created_at: string }>;
  projects: Array<{ id: number; title: string; status: string; total_milestones: number; paid_milestones: number }>;
  proposals: Array<{ id: number; title: string; status: string; discount_percent: number }>;
  notifications: Array<{ id: number; title: string; message: string; link: string; created_at: string; is_read: number }>;
  payments: { total_paid: number; total_pending: number; paid_count: number };
  activeSubscriptions: number;
}

export interface PortalProject {
  id: number;
  title: string;
  description: string | null;
  status: string;
  total_amount: number;
  currency: string;
  total_milestones: number;
  paid_milestones: number;
  active_milestones: number;
  total_paid: number;
  current_milestone_title: string | null;
  created_at: string;
}

export interface PortalProjectDetail {
  project: {
    id: number;
    title: string;
    description: string | null;
    status: string;
    total_amount: number;
    upfront_percent: number;
    currency: string;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
  };
  milestones: Milestone[];
  timeline: TimelineEvent[];
  payments: Payment[];
  proposal: { id: number; title: string; items: Array<{ service_name: string; price: number }> } | null;
}

export interface PortalProposalDetail {
  proposal: {
    id: number;
    title: string;
    status: string;
    discount_percent: number;
    discount_code: string | null;
    discount_type: string;
    discount_deadline: string | null;
    valid_until: string | null;
    created_at: string;
    payment_mode: string | null;
    interest_rate: number | null;
    subscription_interval: string | null;
    subscription_amount: number | null;
  };
  items: ProposalItem[];
}

export interface PortalPaymentsData {
  payments: Array<Payment & { project_title?: string; milestone_title?: string; invoice_number?: string | null }>;
  subscriptions: Array<{ id: number; service_name: string; amount: number; frequency: string; status: string; current_period_end: string; project_title: string }>;
  pendingMilestones: Array<{ id: number; title: string; amount: number; project_title: string }>;
  summary: { total_paid: number; total_pending: number; paid_count: number; pending_count: number };
}

export interface PortalNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  link: string;
  is_read: number;
  created_at: string;
}

export interface PortalNotificationsResponse {
  notifications: PortalNotification[];
  total: number;
  unread: number;
}

export interface CheckoutSessionStatus {
  status: string | null;
  customerEmail: string | null;
  paymentStatus: string | null;
}

export const portalApi = {
  dashboard: () =>
    request<PortalDashboardData>('/portal/dashboard'),

  projects: () =>
    request<{ projects: PortalProject[] }>('/portal/my-projects'),

  project: (id: number) =>
    request<PortalProjectDetail>(`/portal/my-projects/${id}`),

  projectFeedback: (id: number, message: string) =>
    request<{ ok: boolean }>(`/portal/my-projects/${id}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  proposal: (id: number) =>
    request<PortalProposalDetail>(`/portal/my-proposals/${id}`),

  applyDiscountCode: (proposalId: number, code: string) =>
    request<{ valid?: boolean; error?: string; discount_percent?: number; discount_type?: string; discount_deadline?: string }>(`/portal/my-proposals/${proposalId}/apply-code`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  acceptProposal: (proposalId: number, data: { payment_plan: string; installment_months?: number; installment_frequency?: string; installment_amount?: number; interest_total?: number }) =>
    request<{ ok?: boolean; projectId?: number; error?: string }>(`/portal/my-proposals/${proposalId}/accept`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  payments: () =>
    request<PortalPaymentsData>('/portal/my-payments'),

  notifications: () =>
    request<PortalNotificationsResponse>('/portal/my-notifications'),

  markNotificationsRead: (ids?: number[]) =>
    request<{ ok: boolean }>('/portal/my-notifications', {
      method: 'POST',
      body: JSON.stringify(ids && ids.length ? { ids } : { all: true }),
    }),

  checkoutConfig: () =>
    request<{ publishableKey: string }>('/portal/checkout/config'),

  createCheckout: (type: string, id: number) =>
    request<{ clientSecret?: string; error?: string }>('/portal/checkout', {
      method: 'POST',
      body: JSON.stringify({ type, id }),
    }),

  sessionStatus: (sessionId: string) =>
    request<CheckoutSessionStatus>(`/portal/checkout/session-status?session_id=${encodeURIComponent(sessionId)}`),

  invoiceDownloadUrl: (paymentId: number) =>
    `/api/portal/invoices/${paymentId}/download`,
};
